import { MarketGroupData, moveMarketToIndex, TemplateIdsMap } from "@finbackoffice/fe-core";
import {
    convertMarketsIntoMarketGroupsBCFeed,
    sortMarketsInGroupsBCFeed,
    convertMarketsIntoMarketGroups,
    sortMarketsInGroups,
    sortMarketGroups,
    BrowserStorageIdsEnum,
    BrowserStorageContext,
    sortMarketGroupsBC,
} from "@finbackoffice/site-core";
import { useState, useCallback, useContext, useEffect } from "react";
import { FeedScopeEnum } from "utils/constants";
import { IMarketFeedResponse } from "@finbackoffice/clientbff-client";
import { useSportFeedType } from "./useSportFeedType";

// need IMarketFeedResponse from /websocket-client

export const useMarketGroups = (
    gameId: number,
    markets: IMarketFeedResponse[],
    homeTeamName: string,
    awayTeamName: string,
    feed: FeedScopeEnum,
    sportId?: number,
) => {
    const { get, set } = useContext(BrowserStorageContext);
    const { isBCFeed } = useSportFeedType();

    const [marketGroups, setMarketGroups] = useState<MarketGroupData[]>([]);
    const [favMarketGroupIds, setFavMarketGroupIds] = useState<string[]>([]);

    useEffect(() => {
        if (!markets || !homeTeamName || !awayTeamName) {
            setMarketGroups([]);
            return;
        }

        let marketGroupsRecord: Record<string, MarketGroupData>;

        if (isBCFeed) {
            marketGroupsRecord = convertMarketsIntoMarketGroupsBCFeed(
                markets,
                homeTeamName,
                awayTeamName,
            );
            marketGroupsRecord = sortMarketsInGroupsBCFeed(marketGroupsRecord);
        } else {
            marketGroupsRecord = convertMarketsIntoMarketGroups(
                markets,
                homeTeamName,
                awayTeamName,
            );
            marketGroupsRecord = sortMarketsInGroups(marketGroupsRecord);
        }

        let marketGroupsArray = Object.values(marketGroupsRecord);

        if (isBCFeed) {
            marketGroupsArray.sort(sortMarketGroupsBC(favMarketGroupIds));
        } else {
            marketGroupsArray.sort(sortMarketGroups(favMarketGroupIds));
            if (feed === FeedScopeEnum.prematch) {
                marketGroupsArray = moveMarketToIndex(
                    marketGroupsArray,
                    1,
                    TemplateIdsMap.WHICH_TEAM_WINS_THE_COIN_TOSS,
                );
            }
        }

        /* console.log(marketGroupsArray);
        console.log(markets); */

        setMarketGroups(marketGroupsArray);
    }, [awayTeamName, favMarketGroupIds, feed, homeTeamName, isBCFeed, markets]);

    useEffect(() => {
        if (feed === FeedScopeEnum.live) {
            const savedFavMarkets = get(BrowserStorageIdsEnum.FAVORITE_LIVE_MARKETS_IDS);
            if (gameId && savedFavMarkets && sportId) {
                setFavMarketGroupIds(savedFavMarkets[sportId] || []);
            }
        } else if (feed === FeedScopeEnum.prematch) {
            const savedFavMarkets = get(BrowserStorageIdsEnum.FAVORITE_PREMATCH_MARKETS_IDS);
            if (gameId && savedFavMarkets && sportId) {
                setFavMarketGroupIds(savedFavMarkets[sportId] || []);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feed, gameId, sportId]);

    const getUpdatedFavorites = useCallback(
        (storage: Storage, marketType: string) => {
            if (sportId) {
                if (storage && storage.hasOwnProperty(sportId)) {
                    const index: number = storage[sportId].indexOf(marketType);

                    if (index === -1) {
                        storage[sportId].push(marketType);
                    } else {
                        storage[sportId].splice(index, 1);
                    }
                } else {
                    storage[sportId] = [marketType];
                }
            }

            return storage;
        },
        [sportId],
    );

    const handleFavoriteClick = useCallback(
        (marketType: string) => {
            if (sportId) {
                const savedFavPrematchMarkets = get(
                    BrowserStorageIdsEnum.FAVORITE_PREMATCH_MARKETS_IDS,
                );
                const savedFavLiveMarkets = get(BrowserStorageIdsEnum.FAVORITE_LIVE_MARKETS_IDS);
                let storage =
                    feed === FeedScopeEnum.prematch
                        ? (savedFavPrematchMarkets ?? {})
                        : (savedFavLiveMarkets ?? {});

                storage = getUpdatedFavorites(storage, marketType);

                setFavMarketGroupIds(storage[sportId]);
                set(
                    feed === FeedScopeEnum.prematch
                        ? BrowserStorageIdsEnum.FAVORITE_PREMATCH_MARKETS_IDS
                        : BrowserStorageIdsEnum.FAVORITE_LIVE_MARKETS_IDS,
                    JSON.stringify(storage),
                );
            }
        },
        [feed, get, getUpdatedFavorites, set, sportId],
    );

    const isMarketGroupFav = useCallback(
        (id: string) => favMarketGroupIds.includes(id),
        [favMarketGroupIds],
    );

    return { marketGroups, handleFavoriteClick, isMarketGroupFav };
};
